import * as echarts from "echarts";

export interface RenderSvgOptions {
  option: any;
  width: number;
  height: number;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 3000;

const extractSvgContent = (chart: any): string => {
  const zr = chart.getZr();

  if (zr.painter._svgDom) {
    return zr.painter._svgDom.outerHTML;
  }

  if (zr.painter._svgRoot) {
    return zr.painter._svgRoot.outerHTML;
  }

  throw new Error("SVG DOM not found in ECharts painter");
};

export const renderEchartToSvg = ({
  option,
  width,
  height,
  timeoutMs = DEFAULT_TIMEOUT_MS
}: RenderSvgOptions): Promise<string> => {
  return new Promise((resolve, reject) => {
    const tempDiv = document.createElement("div");
    tempDiv.style.width = `${width}px`;
    tempDiv.style.height = `${height}px`;
    tempDiv.style.position = "fixed";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    let chart: any;
    let settled = false;
    let timeoutId: number | undefined;

    const finish = (isError: boolean, value: unknown): void => {
      if (settled) {
        return;
      }
      settled = true;

      try {
        if (chart) {
          chart.off("finished", onFinished);
          chart.dispose();
        }
      } catch {
        // Ignore cleanup errors.
      }

      try {
        if (tempDiv.parentNode) {
          tempDiv.parentNode.removeChild(tempDiv);
        }
      } catch {
        // Ignore cleanup errors.
      }

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      if (isError) {
        reject(value);
      } else {
        resolve(value as string);
      }
    };

    const onFinished = (): void => {
      try {
        finish(false, extractSvgContent(chart));
      } catch (err) {
        finish(true, err);
      }
    };

    try {
      chart = echarts.init(tempDiv, undefined, { renderer: "svg" });
      chart.on("finished", onFinished);

      timeoutId = window.setTimeout(() => {
        finish(
          true,
          new Error(`ECharts SVG render timeout after ${timeoutMs}ms`)
        );
      }, timeoutMs);

      chart.setOption(option);
    } catch (err) {
      finish(true, err);
    }
  });
};
